<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import AgentBotFunctionsAPI from 'dashboard/api/agentBotFunctions';
import BotInvocationAPI from 'dashboard/api/botInvocation';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  inboxId: { type: [Number, String], required: true },
});

const store = useStore();
const { t } = useI18n();
const currentChat = useMapGetter('getSelectedChat');

const functions = ref([]);
const isLoading = ref(false);
const activeBot = computed(
  () =>
    currentChat.value?.meta?.assignee_agent_bot ||
    (currentChat.value?.meta?.assignee_type === 'AgentBot'
      ? currentChat.value?.meta?.assignee
      : null)
);
const activeFunction = computed(
  () => currentChat.value?.meta?.active_agent_bot_function
);

const inboxBot = computed(() => {
  return store.getters['agentBots/getActiveAgentBot'](Number(props.inboxId));
});

const loadFunctions = async () => {
  const bot = inboxBot.value;
  if (!bot?.id) {
    functions.value = [];
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await AgentBotFunctionsAPI.getFunctions(bot.id);
    functions.value = (data || []).filter(f => f.active);
  } catch (_e) {
    functions.value = [];
  } finally {
    isLoading.value = false;
  }
};

const invokeFunction = async fn => {
  const bot = inboxBot.value;
  if (!bot?.id) return;
  try {
    await BotInvocationAPI.invoke({
      conversationId: props.conversationId,
      agentBotId: bot.id,
      functionId: fn.id,
    });
    useAlert(t('CONVERSATION.BOT_INVOKE.SUCCESS'));
    await store.dispatch('getConversation', props.conversationId, {
      root: true,
    });
  } catch (_e) {
    useAlert(t('CONVERSATION.BOT_INVOKE.ERROR'));
  }
};

const releaseBot = async () => {
  try {
    await BotInvocationAPI.release(props.conversationId);
    useAlert(t('CONVERSATION.BOT_INVOKE.RELEASE_SUCCESS'));
    await store.dispatch('getConversation', props.conversationId, {
      root: true,
    });
  } catch (_e) {
    useAlert(t('CONVERSATION.BOT_INVOKE.RELEASE_ERROR'));
  }
};

onMounted(async () => {
  await store.dispatch('agentBots/fetchAgentBotInbox', props.inboxId);
  await loadFunctions();
});
</script>

<template>
  <div>
    <div v-if="!inboxBot?.id && !isLoading" class="text-xs text-n-slate-11">
      {{ $t('CONVERSATION.BOT_INVOKE.NOT_CONFIGURED') }}
    </div>
    <div v-else-if="activeBot" class="flex flex-col gap-2 mb-2">
      <p class="text-xs text-n-slate-11">
        {{
          $t('CONVERSATION.BOT_INVOKE.ACTIVE', {
            bot: activeBot.name,
            fn: activeFunction?.name || 'default',
          })
        }}
      </p>
      <NextButton
        xs
        :label="$t('CONVERSATION.BOT_INVOKE.RELEASE')"
        @click="releaseBot"
      />
    </div>
    <div v-else-if="functions.length" class="flex flex-col gap-1">
      <NextButton
        v-for="fn in functions"
        :key="fn.id"
        xs
        slate
        class="justify-start"
        :label="fn.name"
        @click="invokeFunction(fn)"
      />
    </div>
    <p v-else-if="!isLoading" class="text-xs text-n-slate-11">
      {{ $t('CONVERSATION.BOT_INVOKE.NO_FUNCTIONS') }}
    </p>
  </div>
</template>
